/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Observable, Subject, Subscription, take, takeUntil } from 'rxjs';
import { TabDescription } from 'libs/sysque/toolbelt/data-access/src/lib/domain/tab-description';
import { Item } from 'libs/sysque/toolbelt/data-access/src/lib/domain/item';
import { ItemReorderInfo } from '../../../../../data-access/src/lib/domain/item-reorder-info';
import { DxContextMenuComponent, DxSelectBoxComponent } from 'devextreme-angular';
import {
  CATEGORY_SCOPE_FACADE_TOKEN,
  ICategoryScopeFacade,
} from '../../../../../../toolbelt/data-access/src/lib/domain/facades-models/category-scope-facade-interface';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import { MenuItemProperties } from 'libs/sysque/toolbelt/feature/src/lib/models/menu-item-properties';
import { FittingDefaultReturnCodes } from '../../models/fitting-default-return-codes';
import { GlobalConstants } from '../../constants/global-constants';
import { FittingEnableDisableReturnCodes } from '../../models/fitting-enable-disable-return-codes';
import { DeleteItemReturnCodes } from '../../models/delete-item-return-codes';
import { ImageDescriptionType } from '../../../../../../shared/ui/src/lib/enums/image-description-type';
import { IImageLabel } from '../../models/image-label-interface';
import { ITooltip } from '../../../../../../shared/ui/src/lib/components/sysque-tooltip/sysque-tooltip-types';
import { DockableDrawingObject } from '../../models/dockable-drawing-object';
import { FamilyInformationItem } from '../../models/family-information-model';
import { MenuItems } from '../../models/menu-items';
import { IFamilyInformationItem } from '../add-family-information/add-family-information-types';
import { SysQueCopyPasteService } from '../../../../../utils/src/lib/sysque-copy-paste/sysque-copy-paste.service';
import { AcrylicSurfaceService } from '../../acrylic-surface/acrylic-surface.service';

@Component({
  selector: 'sysque-category-scope',
  templateUrl: './category-scope.component.html',
  styleUrls: ['./category-scope.component.scss'],
  standalone: false
})
export class CategoryScopeComponent implements OnInit, OnDestroy {
  @Input() addFittingMode: boolean;
  @Input() categoryList$: Observable<TabDescription[]>;
  @Input() manageMode: boolean;
  @Input() selectedCategory: string;
  @Input() fittingList: Item[];
  @Input() selectedSystem: any;
  @Input() key: string;
  @Output() copiedItemListChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() showInEnclosingFolder: EventEmitter<Item> = new EventEmitter<Item>();
  @Output() categoryChanged: EventEmitter<any> = new EventEmitter<any>();

  public windowName: string;
  public fittings: Item[];
  public defaultItems: MenuItems[];
  public isSelectBoxVisible = false;
  public isMenuUpdated = false;
  public isLoading = false;
  public isError = false;
  public progressBarTitle: string;
  public progressBarBody: string;
  public dialogTitle = '';
  public dialogBody = '';
  public buttonName = '';
  public isButtonNoDisabled = true;
  public rightClickOption = '';
  public itemName = '';
  public imageLabel: IImageLabel;
  public isDrawing = false;
  public isLicenseValid: boolean;
  public releaseVersion: string;
  public reporIssueTooltip = GlobalConstants.FeatureIncludedOnlyInSubscription;

  // this one can be any,
  // because will be used in the clearTimeout-browser-function
  public showTooltip: boolean = true;
  public copiedItemNameList: string[] = [];
  public defaultCategory: string;
  public hoveredItemIndex: number | null = null;

  private dialogTypeSubscription: Subscription;
  private dockedState: SysqueElectricalState = new SysqueElectricalState();
  public isCopyState: boolean;
  public duplicateFitting: Item;
  public tooltipTimeout: ReturnType<typeof setTimeout>;
  public tooltipShowTimeout: ReturnType<typeof setTimeout>;
  public isTooltipOn: boolean;
  public selectedTakeOffItem: Item = null;
  public showTakeOffDialog: boolean = false;
  public takeOffLengthHeader: string;
  private takeOffLength: string = '';  

  private readonly closed$ = new Subject<void>();
  private previousSelectedSystem: string;

  draggedItemIndex: number;
 
  constructor(
    @Inject(CATEGORY_SCOPE_FACADE_TOKEN) public categoryFacade: ICategoryScopeFacade,
    public copyPasteService: SysQueCopyPasteService,
    public cdr: ChangeDetectorRef,
    private acrylicSurfaceService: AcrylicSurfaceService
  ) {}

  public ngOnDestroy(): void {
    window.removeEventListener(GlobalConstants.STORAGE, this.handleStorageEvent.bind(this));
    this.dialogTypeSubscription?.unsubscribe();
    this.closed$.next();
    this.closed$.complete();
  }

  public ngOnInit(): void {
    this.categoryFacade
      .isTooltipOn()
      .pipe(take(1))
      .subscribe((value) => {
        this.isTooltipOn = value;
      });
    if (this.categoryList$) {
      this.categoryList$.subscribe((categories) => {
        if (categories && categories.length > 0) {
          this.defaultCategory = categories[0].tabName;
        }
      });
    }
    this.categoryFacade.windowKey.next(this.key);
    this.categoryFacade.registerControllers();
    window.addEventListener(GlobalConstants.STORAGE, this.handleStorageEvent.bind(this));
  }

  public onItemClick(item: Item): Promise<void> {
    // If the active window from where the item was clicked is not Dockable return
    if (this.key !== GlobalConstants.DockableKey || this.showTakeOffDialog || item.isDisabled) {
      return;
    }
 
    this.dockedState = this.categoryFacade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
    if (
      this.categoryFacade.getSysqueConnectState() &&
      this.dockedState.selectedCategory !== GlobalConstants.Conduit &&
      !this.isTrayItem(this.dockedState.selectedCategory)
    ) {
      return;
    }

    const circuitsLabels = (this.dockedState.checkBoxConfigs || [])
      .filter((config) => config && config.order !== undefined && config.label !== undefined)
      .sort((a, b) => a.order - b.order)
      .map((config) => config.label)
      .join('_');

    // Set value 0 for the options that are undefined, null or empty string
    const cableTrayNumberText = (this.dockedState.cableTrayNumberText || []).map(option => ({
      ...option,
      value:
        option.value === undefined || option.value === null ||
        (typeof option.value === 'string' && option.value === '') ? 0 : option.value
    }));

    const dockableDrawingObjectProperties: DockableDrawingObject = {
      systemSelected: this.dockedState.selectedSystem,
      categorySelected: this.dockedState.selectedCategory,
      itemName: item.name,
      itemType: item.itemType,
      isProcessOnlyFitting: this.dockedState.onlyFittingProcess,
      isSysQueConnectOn: this.categoryFacade.getSysqueConnectState(),
      isHangerNeeded: this.dockedState.isHangerNeeded,
      cableTrayNumberText: cableTrayNumberText,
      projectName: this.dockedState.projectName,
      panelName: this.dockedState.panelName,
      wireFillSpec: this.dockedState.wirefillSpec,
      circuits: circuitsLabels,
      takeOffLength: this.takeOffLength
    };

    this.acrylicSurfaceService.show();
    const result = this.categoryFacade.sendDrawingOptions(GlobalConstants.DrawingCommand, dockableDrawingObjectProperties);
    result.subscribe((response) => {
      if(!response) {
        this.acrylicSurfaceService.hide();
      }
    });

    this.takeOffLength = '';
  }

  @ViewChildren(DxContextMenuComponent) contextMenus: QueryList<DxContextMenuComponent>;
  @ViewChild(DxSelectBoxComponent) dxSelectBox: DxSelectBoxComponent;
  @ViewChild('selectBoxContainer', { read: ElementRef }) selectBoxContainer: ElementRef;
  @ViewChild('categoryContainer') categoryContainer: ElementRef;
  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    this.contextMenus?.forEach((contextMenu) => {
      if (contextMenu.visible) {
        contextMenu.visible = false;
        this.isMenuUpdated = false;
      }
    });
    this.showTooltip = true;

    this.fittingList?.forEach((fitting) => {
      fitting.tooltipVisible = false;
    });

    if (this.dxSelectBox && this.dxSelectBox.opened) {
      const clickedInside = this.selectBoxContainer.nativeElement.contains(event.target);
      if (!clickedInside) {
        this.dxSelectBox.opened = false;
      }
    }
  }

  @HostListener('document:contextmenu', ['$event'])
  public onDocumentRightClick(event: MouseEvent): void {
    this.isMenuUpdated = false;
    this.showTooltip = false;
    this.selectedTakeOffItem = null;
  }

  public isReportIssueDisabled(itemName: string): boolean {
    return itemName.toLocaleLowerCase() === GlobalConstants.ReportIssue && !this.isLicenseValid;
  }

  public getImageLabel(item: Item): IImageLabel {
    this.imageLabel = { label: '', hasDescription: true };
    switch (item.imageDescriptionType) {
      //case image with family name
      case ImageDescriptionType.IFN: {
        this.imageLabel.label = item.name;
        break;
      }
      //case image with description instance
      case ImageDescriptionType.IDS: {
        this.imageLabel.label = item.descriptionInstance;
        break;
      }
      //case image with description type
      case ImageDescriptionType.IDT: {
        this.imageLabel.label = item.descriptionType;
        break;
      }
      //case image with no description
      default: {
        this.imageLabel.label = '';
        this.imageLabel.hasDescription = false;
        break;
      }
    }
    return this.imageLabel;
  }

  public async updateMenuItems(item: Item): Promise<void> {
    if (this.isMenuUpdated || !item) {
      return;
    }
    
    this.dockedState = this.categoryFacade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);

    if (!this.dockedState.releaseVersion) {
        const releaseVersionLicenseStatus = this.categoryFacade.getLocalStorage(GlobalConstants.DOCK_RELEASE_VERSION_LICENSE_STATUS);
            this.releaseVersion = releaseVersionLicenseStatus.releaseVersion;
            this.isLicenseValid = releaseVersionLicenseStatus.isLicenseValid;
    } else {
        this.releaseVersion = this.dockedState.releaseVersion;
        this.isLicenseValid = this.dockedState.isLicenseValid;
    }
    
    if (this.dockedState.categoryList && this.dockedState.categoryList.length > 0) {
      this.defaultCategory = this.dockedState.categoryList[0].tabName;
    } 

    switch (this.key) {
      case GlobalConstants.ManageSrcKey: {
        this.windowName = GlobalConstants.ManageMode;
        break;
      }
      case GlobalConstants.AddFittingsKey: {
        this.windowName = GlobalConstants.AddFittings;
        break;
      }
      case GlobalConstants.DockableKey: {
        this.windowName = GlobalConstants.Dockable;
        break;
      }
    }
    item.isSysQueConnectOn = this.categoryFacade.getSysqueConnectState();
    this.dockedState = this.categoryFacade.getDockedState();   
    const result = this.categoryFacade.getLocalStorage(GlobalConstants.GAUGE_DIAMETER_AND_SIZE);
    let gaugeVariable : string;
   if (!this.isTrayItem(this.dockedState.selectedCategory)) {
     gaugeVariable = [GlobalConstants.DIAMETER, result?.conduit].join(',');
   } else {
     gaugeVariable = [GlobalConstants.HEIGHT_AND_WIDTH, result?.height, result?.width].join(',');
   }
    this.defaultItems = this.categoryFacade.menuItemsUpdate(
      item,
      this.defaultCategory,
      this.windowName,
      this.selectedCategory,
      gaugeVariable,
      this.releaseVersion
    );
    this.isMenuUpdated = true;
  }

  public onContextMenuShowing(event, item): void {
    this.updateMenuItems(item);
    this.contextMenus.forEach((contextMenu) => {
      if (contextMenu !== event.component) {
        contextMenu.visible = false;
      }
    });
    this.showTooltip = false;
  }

  public onContextMenuItemClick($event, item: Item, i: number): void {
    const itemClicked = $event.itemData;
    this.dockedState = this.categoryFacade.getDockedState();
    this.isCopyState = false;
    this.selectedCategory = this.dockedState.selectedCategory;
    if (this.contextMenuDrawingOption(itemClicked, item)) {
      return;
    }
    switch (itemClicked.text.toLowerCase()) {
      case GlobalConstants.DownloadLatestFitting: {
        this.downloadFittingLogic(item, i);
        break;
      }
      case GlobalConstants.SetAsDefault:
      case GlobalConstants.RemoveDefault: {
        this.setRemoveDefaultLogic($event, item, i);
        break;
      }
      case GlobalConstants.Enable:
      case GlobalConstants.Disable: {
        this.enableDisableFittingLogic($event, item, i);
        break;
      }
      case GlobalConstants.Copy: {
        this.clearCopiedItems();
        this.setStyle(item);
        this.isCopyState = true;
        this.updateManageDest(item);
        this.copiedItemListChanged.emit(item);
        break;
      }
      case GlobalConstants.Edit: {
        const famInfoItem = item as IFamilyInformationItem;
        famInfoItem.isFromEdit = true;
        famInfoItem.isFromDuplicate = false;
        famInfoItem.isFromAdd = false;
        this.categoryFacade.updateLocalStorage(GlobalConstants.HangerCopySetUp, false);
        this.categoryFacade.getAddFamilyInformationDialogType(famInfoItem);
        this.acrylicSurfaceService.show();
        break;
      }
      case GlobalConstants.Duplicate: {
        this.duplicateFitting = item;
        this.categoryFacade.updateLocalStorageForEditDuplicateAlert(
          GlobalConstants.SHOW_EDIT_DUPLICATE_ITEM_ALERT,
          JSON.stringify(true)
        );
        break;
      }
      case GlobalConstants.Delete: {
        this.deleteItemlogic(item.name);
        break;
      }
      case GlobalConstants.ShowInEnclosingFolder: {
        this.showInEnclosingFolder.emit(item);
        break;
      }

      case GlobalConstants.TAKE_OFF_BY_LENGTH_KEY: {
        this.takeOffLengthHeader =
          this.dockedState.systemList[0].unitType === 'IMPERIAL'
            ? GlobalConstants.TakeOffByLength + '(inch)'
            : GlobalConstants.TakeOffByLength + '(mm)';
        this.selectedTakeOffItem = item;
        this.showTakeOffDialog = true;
        break;
      }
      case GlobalConstants.ReportIssue: {
        if(!this.isReportIssueDisabled(GlobalConstants.ReportIssue))
        this.categoryFacade.showReportIssue(item.id).subscribe();
        break;
      }
    }
  } 

  public getTakeOffLengthForSysQueConnect(inputTakeOffLength: string): void {
    this.takeOffLength = inputTakeOffLength;
    this.showTakeOffDialog = false;
    this.onItemClick(this.selectedTakeOffItem);
    this.closeTakeOffWindow();
  }

  public closeTakeOffWindow(): void {
    this.showTakeOffDialog = false;
    this.selectedTakeOffItem = null;
  }

  public updateManageDest(item?: Item): void {
    if (this.key === GlobalConstants.ManageSrcKey) {
      this.categoryFacade.updateManageSrcStateFittingListProperty(this.dockedState);
      if (!this.isCopyState) {
        this.categoryFacade.updateManageDestStateFittingListProperty(this.dockedState);
      } else {
        this.categoryFacade.updateManageDestStateFittingListPropertyForCopiedItem(this.dockedState, item);
      }
    }
  }

  //#region Download fitting logic

  public downloadFittingLogic(item: Item, i: number): void {
    this.setDialogProperties(item.name);
    this.cdr.markForCheck();

    setTimeout(() => {
      this.isError = !this.categoryFacade.downloadFitting(item.id, this.releaseVersion, this.selectedSystem);
      if (!this.isError) {
        this.updateTootltip(item.name, i);
      } else {
        this.setErrorDialogMessages();
      }
      this.isLoading = false;
      this.cdr.markForCheck();
    });
  }

  public setDialogProperties(itemName: string): void {
    this.isLoading = true;
    this.progressBarTitle = GlobalConstants.Downloading;
    this.progressBarBody = itemName;
  }

  public async updateTootltip(itemName: string, i: number): Promise<void> {
    this.fittings = await this.categoryFacade.getFittings(this.selectedSystem, this.selectedCategory);
    for (let index = 0; index < this.fittings.length; index++) {
      if (this.fittings[index].name === itemName) {
        this.fittingList[i] = this.fittings[index];
        this.fittingList[i].tooltipItems = this.categoryFacade.getTooltipItemInfo(
          this.fittingList[i],
          this.defaultCategory
        );
        break;
      }
    }
    this.dockedState.fittingList = this.fittingList;
    this.categoryFacade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
    this.dockedState = this.categoryFacade.getDockedState();
    this.updateManageDest();
  }

  public setErrorDialogMessages(): void {
    this.dialogTitle = GlobalConstants.TitleForUnableToDownloadLatestFitting;
    this.dialogBody = GlobalConstants.BodyForUnableToDownloadLatestFitting;
    this.buttonName = GlobalConstants.Dismiss;
    this.isButtonNoDisabled = true;
  }

  //#endregion

  //#region Set/Remove default & enable/disable logic

  public async setRemoveDefaultLogic($event, item: Item, i: number): Promise<void> {
    if (!item.name || !this.selectedCategory || !this.selectedSystem) {
      this.caseErrorDefault();
      return;
    }
    const menuItemProperties: MenuItemProperties = {
      categoryName: this.selectedCategory,
      systemName: this.selectedSystem,
      itemName: item.name,
    };
    const isDefault = $event.itemData.text.toLowerCase() === GlobalConstants.SetAsDefault;
    let result: FittingDefaultReturnCodes = await this.categoryFacade.setRemoveDefaultFitting(
      menuItemProperties,
      isDefault
    );
    switch (result) {
      case FittingDefaultReturnCodes.SetAsDefault:
      case FittingDefaultReturnCodes.RemoveAsDefault: {
        this.casesSetOrRemoveDefault(item, i, isDefault);
        break;
      }
      case FittingDefaultReturnCodes.NotAvailable: {
        this.caseNotAvailableDefault(item.name);
        break;
      }
      case FittingDefaultReturnCodes.NoSystemDef: {
        this.caseNoSystemDefDefault();
        break;
      }
      case FittingDefaultReturnCodes.Error: {
        this.caseErrorDefault();
        break;
      }
    }
  }

  public casesSetOrRemoveDefault(item: Item, i: number, isDefault: boolean): void {
    item.isDefault = isDefault;
    this.updateItemsList();
  }

  public async updateItemsList(): Promise<void> {
    this.fittingList = await this.categoryFacade.getFittings(this.selectedSystem, this.selectedCategory);
    for (let index = 0; index < this.fittingList.length; index++) {
      this.fittingList[index].tooltipItems = this.categoryFacade.getTooltipItemInfo(
        this.fittingList[index],
        this.defaultCategory
      );
    }
    this.dockedState.fittingList = this.fittingList;
    this.categoryFacade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
    this.dockedState = this.categoryFacade.getDockedState();

    this.updateManageDest();
    this.cdr.markForCheck();
  }

  public caseNotAvailableDefault(itemName: string): void {
    this.isError = true;
    this.dialogTitle = GlobalConstants.TitleForDefaultPosition;
    this.dialogBody = itemName + GlobalConstants.BodyForUnavailableDefaultPosition;
    this.buttonName = GlobalConstants.Dismiss;
    this.isButtonNoDisabled = true;
  }

  public caseNoSystemDefDefault(): void {
    this.isError = true;
    this.dialogTitle = GlobalConstants.TitleForDefaultPosition;
    this.dialogBody = GlobalConstants.BodyForNoSystemDef;
    this.buttonName = GlobalConstants.Dismiss;
    this.isButtonNoDisabled = true;
  }

  public caseErrorDefault(): void {
    this.isError = true;
    this.dialogTitle = GlobalConstants.TitleForDefaultPosition;
    this.dialogBody = GlobalConstants.BodyForErrorDefaultPosition;
    this.buttonName = GlobalConstants.Dismiss;
    this.isButtonNoDisabled = true;
  }

  //#endregion

  //#region Enable/Disable logic

  public async enableDisableFittingLogic($event, item: Item, i: number): Promise<void> {
    if (!item.name || !this.selectedCategory || !this.selectedSystem) {
      this.caseErrorEnableDisable();
      return;
    }
    const menuItemProperties: MenuItemProperties = {
      categoryName: this.selectedCategory,
      systemName: this.selectedSystem,
      itemName: item.name,
    };
    let result: FittingEnableDisableReturnCodes = await this.categoryFacade.enableDisableFitting(menuItemProperties);
    switch (result) {
      case FittingEnableDisableReturnCodes.Enable:
      case FittingEnableDisableReturnCodes.Disable: {
        this.casesEnableOrDisable();
        break;
      }
      case FittingEnableDisableReturnCodes.NoSystemDef: {
        this.caseNoSystemDefEnableDisable();
        break;
      }
      case FittingEnableDisableReturnCodes.Error: {
        this.caseErrorEnableDisable();
        break;
      }
    }

    this.cdr.markForCheck();
  }

  public casesEnableOrDisable(): void {
    this.updateItemsList();
  }

  public caseNoSystemDefEnableDisable(): void {
    this.isError = true;
    this.dialogTitle = GlobalConstants.TitleForDisableEnablePosition;
    this.dialogBody = GlobalConstants.BodyForNoSystemDef;
    this.buttonName = GlobalConstants.Dismiss;
    this.isButtonNoDisabled = true;
  }

  public caseErrorEnableDisable(): void {
    this.isError = true;
    this.dialogTitle = GlobalConstants.TitleForDisableEnablePosition;
    this.dialogBody = GlobalConstants.BodyForErrorDisableEnablePosition;
    this.buttonName = GlobalConstants.Dismiss;
    this.isButtonNoDisabled = true;
  }

  //#endregion

  //#region Copy logic

  public setStyle(item: Item): void {
    this.copyPasteService.copiedItemsName$.pipe(takeUntil(this.closed$)).subscribe((copiedItems) => {
      this.copiedItemNameList = copiedItems;
    });
    item.isCopyState = true;

    const itemPosition = 's_' + item.displayPosition;
    if (!this.copiedItemNameList.includes(itemPosition)) {
      this.copiedItemNameList.push(itemPosition);
    }
    this.copyPasteService.setCopiedItemsName(this.copiedItemNameList);
  }

  public resetStyle(item: Item, cancel: boolean): void {
    let itemIdList: string[] = [];
    this.copyPasteService.copiedItemsName$.pipe(takeUntil(this.closed$)).subscribe((copiedItems) => {
      itemIdList = copiedItems;
    });
    if (cancel) {
      this.cancelClick(itemIdList, item);
    } else {
      this.replaceItems(itemIdList);
    }
  }

  public replaceItems(itemIdList: string[]): void {
    if (this.key === GlobalConstants.ManageDestKey) {
      let state = this.categoryFacade.getManageSrcState();
      itemIdList.forEach((element) => {
        const displayPosition = element.split('_')[1];
        state.fittingList.find((item) => item.displayPosition === displayPosition).isCopyState = false;
      });

      this.categoryFacade.updateManageSrcStateFittingListProperty(state);
    } else {
      itemIdList.forEach((element) => {
        const displayPosition = element.split('_')[1];
        this.fittingList.find((item) => item.displayPosition === displayPosition).isCopyState = false;
      });
    }
    this.copyPasteService.setCopiedItemsName([]);
  }

  public cancelClick(itemIdList: string[], item: Item): void {
    const fIndex = itemIdList.findIndex((element) => element === 's_' + item.displayPosition);
    itemIdList.splice(fIndex, 1);
    this.copyPasteService.setCopiedItemsName(itemIdList);
    item.isCopyState = false;
  }

  //#endregion

  //#region Delete logic

  public deleteItemlogic(itemName: string): void {
    this.isError = true;
    this.dialogTitle = GlobalConstants.TitleForDeleteFitting;
    this.dialogBody = GlobalConstants.BodyForDeleteFitting;
    this.buttonName = GlobalConstants.Yes;
    this.isButtonNoDisabled = false;
    this.rightClickOption = GlobalConstants.Delete;
    this.itemName = itemName;
  }

  public responsesOfDelete(response: DeleteItemReturnCodes): void {
    switch (response) {
      case DeleteItemReturnCodes.Success: {
        this.updateItemsList();
        this.isError = false;
        break;
      }
      case DeleteItemReturnCodes.NotAvailable: {
        this.caseNotAvailableDelete();
        break;
      }
      case DeleteItemReturnCodes.Error: {
        this.caseErrorDelete();
        break;
      }
    }
    this.cdr.markForCheck();
  }

  public caseNotAvailableDelete(): void {
    this.isError = true;
    this.dialogTitle = GlobalConstants.TitleForUnableDelete + this.selectedCategory + ' is required.';
    this.dialogBody = GlobalConstants.BodyForUnableDelete + this.selectedCategory;
    this.buttonName = GlobalConstants.Dismiss;
    this.isButtonNoDisabled = true;
  }

  public caseErrorDelete(): void {
    this.isError = true;
    this.dialogTitle = GlobalConstants.TitleForDeleteFitting;
    this.dialogBody = GlobalConstants.BodyForErrorDelete;
    this.buttonName = GlobalConstants.Dismiss;
    this.isButtonNoDisabled = true;
  }

  public deleteItemYes(): void {
    if (!this.itemName || !this.selectedCategory || !this.selectedSystem) {
      this.caseErrorDelete();
      return;
    }
    const menuItemProperties: MenuItemProperties = {
      categoryName: this.selectedCategory,
      systemName: this.selectedSystem,
      itemName: this.itemName,
    };
    this.categoryFacade
      .deleteItem(menuItemProperties)
      .pipe(takeUntil(this.closed$))
      .subscribe((response) => {
        this.responsesOfDelete(response);
      });
    this.updateManageDest();
    this.rightClickOption = '';
  }

  //#endregion

  public handleComplete(e) {
    alert('Completed');
  }

  public onOkClick(): void {
    if (this.buttonName === GlobalConstants.Dismiss) {
      this.isError = false;
      return;
    }
    if (this.rightClickOption === GlobalConstants.Delete) {
      this.deleteItemYes();
    }
    this.isButtonNoDisabled = true;
  }

  public onNoClick(): void {
    this.isError = false;
    this.isButtonNoDisabled = true;
  }

  public onCategoryChanged(e: any): void {
    this.selectedCategory = e.value;
    this.categoryChanged.emit(e);
  }

  public tooltipMouseEnter(i: number): void {
    this.categoryFacade.getLocalStorageUpdate().next('tooltipenter');

    this.fittingList.forEach((fitting, index) => {
      if (index !== i) {
        fitting.tooltipVisible = false;
      }
    });

    if (this.tooltipShowTimeout) {
      clearTimeout(this.tooltipShowTimeout);
    }

    this.tooltipShowTimeout = setTimeout(() => {
      this.fittingList[i].tooltipVisible = true;
      this.hoveredItemIndex = i;
      this.cdr.markForCheck();
    }, 3000);
  }

  public tooltipMouseLeave(e: any, i: number): void {
    if (
      e?.relatedTarget?.classList?.contains('dx-overlay-content') ||
      e?.relatedTarget?.classList?.contains('dx-popover-arrow') ||
      e?.relatedTarget?.closest('.sysque-tooltip')
    ) {
      this.cdr.markForCheck();
      return;
    }

    if (this.tooltipShowTimeout) {
      clearTimeout(this.tooltipShowTimeout);
    }
    this.tooltipTimeout = setTimeout(() => {
      this.fittingList[i].tooltipVisible = false;
      if (this.hoveredItemIndex === i) {
        this.hoveredItemIndex = null;
      }
      this.cdr.markForCheck();
    }, 1000);
  }

  public tooltipContainerMouseEnter(): void {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
    }
  }

  public tooltipContainerMouseLeave(): void {
    this.tooltipTimeout = setTimeout(() => {
      if (this.hoveredItemIndex !== null) {
        this.fittingList[this.hoveredItemIndex].tooltipVisible = false;
        this.hoveredItemIndex = null;
        this.cdr.markForCheck();
      }
    }, 1000);
  }

  public getTooltipInputData(item: Item, key: string): ITooltip {
    return {
      tooltipItems: this.categoryFacade.getTooltipItemInfo(item, this.defaultCategory),
      fittingName: item.name,
      target: '#s_' + item.displayPosition + '-' + key,
      isHidden: item.tooltipVisible,
    };
  }

  public addFittingClickedNotification(): void {
    this.categoryFacade.updateLocalStorage(GlobalConstants.ADD_FITTING_MODE_CLICKED, true);
    this.acrylicSurfaceService.show();
  }

  public handleStorageEvent(event: StorageEvent): void {
    if (!event) return;
    switch (event.key) {
      case GlobalConstants.FAMILY_INFORMATION_RESPONSE:
        if (event.newValue === '') {
          break;
        }
        const response = JSON.parse(event.newValue) as FamilyInformationItem;
        console.log('This is for opening the correct family info dialog for the item we want to add');
        this.categoryFacade.createAddFamilyInformationBasedOnItemType(response.DialogType);
        break;
      case GlobalConstants.SHOW_EDIT_DUPLICATE_ITEM_WINDOW:
        if (event.newValue === '' || JSON.parse(event.newValue) === false) {
          break;
        }
        const famInfoItem = this.duplicateFitting as IFamilyInformationItem;
        famInfoItem.isFromDuplicate = true;
        famInfoItem.isFromEdit = false;
        this.categoryFacade.getAddFamilyInformationDialogType(famInfoItem);
        break;
      case GlobalConstants.IS_TOOLTIP_ON:
        if (event.newValue === null) {
          break;
        }
        this.isTooltipOn = JSON.parse(event.newValue);
        break;
    }
  }   

  public onDragStart(event: DragEvent, index: number): void {
    this.draggedItemIndex = index;
    const target = event.currentTarget as HTMLElement;
    //get the parent who makes the difference between source and destination in application, for example:
    // 'sysque-manage-mode-dest-panel-frame' and 'sysque-manage-mode-src-panel-frame'
    const sourceParent = (target?.parentNode?.parentNode?.parentNode?.parentNode?.parentNode as HTMLElement)?.localName.toString();
    console.log('sourceParent', sourceParent);
    // Use dataTransfer to store the start value for the drag-and-drop lifecycle
    event.dataTransfer?.setData('sourceParent', sourceParent);
    target.classList.add('dragging');
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    target.classList.add('drag-over');
  }

  public onDrop(event: DragEvent, dropIndex: number): void {
    event.preventDefault();
    const draggedIndex = this.draggedItemIndex;
    const target = event.currentTarget as HTMLElement;
    // Retrieve the 'start' value from the dataTransfer object
    const sourceParent = event.dataTransfer?.getData('sourceParent');
    const targetParentName = (
      target?.parentNode?.parentNode?.parentNode?.parentNode?.parentNode as HTMLElement
    )?.localName.toString();
    if (sourceParent !== targetParentName) {  
      return;
    }
    target.classList.remove('drag-over');
    if (draggedIndex !== dropIndex) {
      this.reorderFittings(draggedIndex, dropIndex);
    }
  }

  public onDragLeave(event: DragEvent): void {
    const target = event.currentTarget as HTMLElement;
    target.classList.remove('drag-over');
  }

  public reorderFittings(draggedIndex: number, dropIndex: number): void {
    const movedItem = this.fittingList[draggedIndex];
    this.fittingList.splice(draggedIndex, 1);
    this.fittingList.splice(dropIndex, 0, movedItem);
    setTimeout(() => {
      let reorderedItems: ItemReorderInfo[] = [];
      let displayPosition: number = 0;
      this.fittingList.forEach((item) => {
        item.displayPosition = (displayPosition + 1).toString();
        reorderedItems.push({
          name: item.name,
          itemType: item.itemType,
          specId: item.specId,
          dispPosition: item.displayPosition,
          displayPosition: (displayPosition % 5) + ',' + Math.floor(displayPosition / 5),
        });
        displayPosition++;
      });
      this.dockedState = this.categoryFacade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
      this.categoryFacade.saveNewFittingsOrder(
        reorderedItems,
        this.dockedState.selectedSystem,
        this.dockedState.selectedCategory
      );
    }, 0);
  }  

  private clearCopiedItems(): void {
    if (this.previousSelectedSystem !== this.dockedState.selectedSystem) {
      this.previousSelectedSystem = this.dockedState.selectedSystem;
      this.copyPasteService.setCopiedItemsName([]);
      this.copyPasteService.setCopiedItemsList([]);
      this.copyPasteService.setDuplicateItemsList([]);
      this.copyPasteService.setUniqueCopiedItemsList([]);
    }
  }

   /**
   * Checks if the given shape is a tray item.
   * @param shape - The shape to check.
   * @returns `true` if the shape is a tray item, `false` otherwise.
   */
  private isTrayItem(shape: string): boolean {
    return (
      shape === GlobalConstants.WireWay ||
      shape === GlobalConstants.ChannelCableTray ||
      shape === GlobalConstants.LadderCableTray ||
      shape === GlobalConstants.WireMeshCableTray ||
      shape === GlobalConstants.BusWay
    );
  }

  private contextMenuDrawingOption(optionClicked: { parentId: string; text: string }, item: Item): boolean {
    this.dockedState = this.categoryFacade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);

    let dockableDrawingObjectProperties: DockableDrawingObject = {
      systemSelected: this.dockedState.selectedSystem,
      categorySelected: this.dockedState.selectedCategory,
      itemName: item.name,
      isCutIn: false,
      isSysQueConnectOn: item.isSysQueConnectOn,
    };

    if (optionClicked.parentId) {
      const menuItem = this.defaultItems.find((item) => item.id === optionClicked.parentId);
      dockableDrawingObjectProperties = {
        ...dockableDrawingObjectProperties,
        optionSelected: menuItem.text,
        sizeSelected: optionClicked.text,
        isCutIn: menuItem.text === GlobalConstants.TakeOffCutIn,
      };
    }  else if (
      (this.selectedCategory === GlobalConstants.CustomContent ||
        this.selectedCategory === GlobalConstants.Accessories || this.selectedCategory === GlobalConstants.BuildingLayoutCategory) &&
      optionClicked.text === GlobalConstants.TakeOff
    ) {
      dockableDrawingObjectProperties = {
        ...dockableDrawingObjectProperties,
        optionSelected: optionClicked.text,
        isCutIn: false,
      };
    } else if (
      optionClicked.text !== GlobalConstants.TakeOffAsMultiRackHanger &&
      optionClicked.text !== GlobalConstants.PlaceHanger
    ) {
      return false;
    } else if (optionClicked.text === GlobalConstants.TakeOffAsMultiRackHanger) {
      dockableDrawingObjectProperties = {
        ...dockableDrawingObjectProperties,
        optionSelected: optionClicked.text,
        isCutIn: false,
      };
    } else if (optionClicked.text === GlobalConstants.PlaceHanger) {
      dockableDrawingObjectProperties = {
        ...dockableDrawingObjectProperties,
        optionSelected: optionClicked.text,
      };
    }

    this.acrylicSurfaceService.show();
    this.categoryFacade.sendDrawingOptions(GlobalConstants.RightClickDrawingCommand, dockableDrawingObjectProperties);
    return true;
  }
}
